import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { programsAPI } from '../../services/api';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';
import ImagePickerField from '../../components/ui/ImagePickerField';
import api from '../../services/api';

const TYPE_LABELS  = { bootcamp:'Bootcamp', workshop:'Workshop', certification:'Certification', course:'Course', mentorship:'Mentorship' };
const MODE_LABELS  = { in_person:'In-Person', online:'Online', hybrid:'Hybrid', physical:'In-Person' };

export default function ProgramsPage() {
  const [tab, setTab]           = useState('programs');
  const [programs, setPrograms] = useState([]);
  const [domains, setDomains]   = useState([]);
  const [cohorts, setCohorts]   = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [pagination, setPag]    = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [filter, setFilter]     = useState({ domain:'', status:'' });
  const [cohortFilter, setCohortFilter] = useState({ program_id:'' });
  const [modal, setModal]       = useState(null);
  const [selected, setSel]      = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const [form, setForm] = useState({
    name:'', slug:'', domain_id:'', type:'bootcamp',
    description:'', outcomes:'', duration:'', mode:'in_person',
    status:'ongoing', is_public:true, featured:false, media_url:'',
  });
  const [cohortForm, setCohortForm] = useState({
    program_id:'', cohort_name:'', start_date:'', end_date:'',
    max_capacity:24, location:'', instructor:'', fee_amount:0,
    fee_currency:'RWF', is_free:false, status:'upcoming', schedule_info:'', notes:'',
  });

  const navigate = useNavigate();

  // ── Load programs — strip empty filter params ─────────
  const loadPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page };
      if (filter.domain) params.domain = filter.domain;
      if (filter.status) params.status = filter.status;
      const res = await programsAPI.list(params);
      setPrograms(res.data.data || []); setPag(res.data.pagination);
    } catch { setPrograms([]); } finally { setLoading(false); }
  }, [page, filter]);

  const loadCohorts = useCallback(async () => {
    setLoading(true);
    try {
      const params = cohortFilter.program_id ? { program_id:cohortFilter.program_id } : {};
      const res = await api.get('/programs/cohorts', { params });
      setCohorts(res.data.data || []); setPag(null);
    } catch { setCohorts([]); } finally { setLoading(false); }
  }, [cohortFilter]);

  useEffect(() => {
    programsAPI.domains().then(r=>setDomains(r.data.data||[])).catch(()=>{});
    programsAPI.list({ limit:100 }).then(r=>setAllPrograms(r.data.data||[])).catch(()=>{});
  }, []);

  useEffect(() => {
    if (tab==='programs') loadPrograms();
    else loadCohorts();
  }, [tab, loadPrograms, loadCohorts]);

  useEffect(() => { setPage(1); }, [tab]);

  // ── Program handlers ──────────────────────────────────
  const openCreateProgram = () => {
    setForm({ name:'', slug:'', domain_id:domains[0]?.id||'', type:'bootcamp', description:'', outcomes:'', duration:'', mode:'in_person', status:'ongoing', is_public:true, featured:false, media_url:'' });
    setSel(null); setError(''); setModal('program');
  };
  const openEditProgram = (p) => {
    setForm({
      name:        p.title || p.name,
      slug:        p.slug,
      domain_id:   p.domain_id,
      type:        p.program_type || p.type,
      description: p.description || p.summary || '',
      outcomes:    Array.isArray(p.outcomes) ? p.outcomes.join('\n') : p.outcomes || '',
      duration:    p.duration || '',
      mode:        p.delivery_mode || p.mode || 'in_person',
      status:      p.status || 'ongoing',
      is_public:   p.is_public !== false,
      featured:    p.featured || false,
      media_url:   p.media_url || '',
    });
    setSel(p); setError(''); setModal('program');
  };
  const handleSaveProgram = async () => {
    if (!form.name?.trim()) { setError('Program name is required'); return; }
    setSaving(true); setError('');
    try {
      if (!selected) await programsAPI.create(form);
      else           await programsAPI.update(selected.id, form);
      setModal(null); loadPrograms();
      programsAPI.list({ limit:100 }).then(r=>setAllPrograms(r.data.data||[])).catch(()=>{});
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  // ── Cohort handlers ───────────────────────────────────
  const openCreateCohort = () => {
    setCohortForm({ program_id:allPrograms[0]?.id||'', cohort_name:'Cohort 2025-A', start_date:'', end_date:'', max_capacity:24, location:'MilleHills Hub, Gisenyi, Rubavu', instructor:'', fee_amount:0, fee_currency:'RWF', is_free:false, status:'upcoming', schedule_info:'', notes:'' });
    setSel(null); setError(''); setModal('cohort');
  };
  const openEditCohort = (c) => {
    setCohortForm({ program_id:c.program_id, cohort_name:c.cohort_name, start_date:c.start_date?.slice(0,10)||'', end_date:c.end_date?.slice(0,10)||'', max_capacity:c.max_capacity||24, location:c.location||'', instructor:c.instructor||'', fee_amount:c.fee_amount||0, fee_currency:c.fee_currency||'RWF', is_free:c.is_free||false, status:c.status||'upcoming', schedule_info:c.schedule_info||'', notes:c.notes||'' });
    setSel(c); setError(''); setModal('cohort');
  };
  const handleSaveCohort = async () => {
    if (!cohortForm.program_id)        { setError('Select a program'); return; }
    if (!cohortForm.cohort_name?.trim()) { setError('Cohort name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...cohortForm, start_date:cohortForm.start_date||null, end_date:cohortForm.end_date||null, fee_amount:cohortForm.is_free?0:cohortForm.fee_amount };
      if (!selected) await api.post('/programs/cohorts', payload);
      else           await api.put(`/programs/cohorts/${selected.id}`, payload);
      setModal(null); loadCohorts();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  // ── Columns ───────────────────────────────────────────
  const statusBadge = (v) => {
    const map    = { ongoing:'ACTIVE', draft:'DRAFT', completed:'COMPLETED', archived:'ARCHIVED' };
    const colors = { ongoing:'#3ecf8e', draft:'#6b7280', completed:'#4e9cf5', archived:'#ef4444' };
    const c = colors[v] || '#6b7280';
    return <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:c+'22', color:c, border:`1px solid ${c}44`, textTransform:'uppercase' }}>{map[v]||v}</span>;
  };

  const PROG_COLS = [
    { key:'media_url', label:'', render:v => (
      <div style={{ width:36, height:36, borderRadius:6, overflow:'hidden', background:'var(--bg-elevated)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
        {v ? <img src={v} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'}/> : <span style={{ fontSize:14, opacity:0.3, color:'var(--blue-light)' }}>◐</span>}
      </div>
    )},
    { key:'title', label:'Program', render:(v,row) => (
      <div>
        <div style={{ fontWeight:500 }}>{v||row.name}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.domain_name} · {MODE_LABELS[row.delivery_mode||row.mode]||row.mode}</div>
      </div>
    )},
    { key:'type',     label:'Type',     render:v => <span style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--blue-light)' }}>{TYPE_LABELS[v]||v}</span> },
    { key:'duration', label:'Duration', render:v => <span style={{ fontSize:12, color:'var(--text-secondary)' }}>{v||'—'}</span> },
    { key:'status',   label:'Status',   render:v => statusBadge(v) },
    { key:'_actions', label:'', render:(_,row) => (
      <div style={{ display:'flex', gap:6 }} onClick={e=>e.stopPropagation()}>
        <Button variant="ghost"      size="sm" onClick={()=>openEditProgram(row)}>Edit</Button>
        <Button variant="ghost"      size="sm" onClick={()=>{setCohortFilter({program_id:row.id});setTab('cohorts');}}>Cohorts</Button>
        <Button variant="secondary"  size="sm" onClick={()=>navigate(`/programs/${row.id}/course`)}>📚 Course</Button>
      </div>
    )},
  ];

  const COHORT_COLS = [
    { key:'cohort_name', label:'Cohort', render:(v,row) => (
      <div><div style={{ fontWeight:600 }}>{v}</div><div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.program_title||'—'}</div></div>
    )},
    { key:'start_date', label:'Dates', render:(v,row) => (
      <div style={{ fontSize:12 }}>
        <div>{v?new Date(v).toLocaleDateString('en-RW',{day:'numeric',month:'short',year:'numeric'}):'—'}</div>
        {row.end_date&&<div style={{ color:'var(--text-muted)' }}>→ {new Date(row.end_date).toLocaleDateString('en-RW',{day:'numeric',month:'short'})}</div>}
      </div>
    )},
    { key:'enrolled', label:'Enrolled', render:(v,row) => (
      <div style={{ fontSize:12 }}>
        <span style={{ fontWeight:600, color:'var(--accent)' }}>{v||0}</span>
        <span style={{ color:'var(--text-muted)' }}>/{row.max_capacity||'∞'}</span>
      </div>
    )},
    { key:'location',   label:'Location', render:v=><span style={{ fontSize:11, color:'var(--text-muted)' }}>{v||'—'}</span> },
    { key:'fee_amount', label:'Fee', render:(v,row)=>row.is_free
      ? <span style={{ fontSize:11, color:'#3ecf8e', fontWeight:600 }}>Free</span>
      : <span style={{ fontSize:11 }}>{row.fee_currency} {Number(v||0).toLocaleString()}</span>
    },
    { key:'status', label:'Status', render:v => (
      <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, textTransform:'capitalize',
        background:v==='upcoming'?'rgba(62,207,142,0.12)':v==='active'||v==='ongoing'?'rgba(232,164,58,0.12)':'rgba(107,114,128,0.12)',
        color:v==='upcoming'?'#3ecf8e':v==='active'||v==='ongoing'?'var(--accent)':'#6b7280',
      }}>{v}</span>
    )},
    { key:'_actions', label:'', render:(_,row)=>(
      <div style={{ display:'flex', gap:6 }} onClick={e=>e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={()=>openEditCohort(row)}>Edit</Button>
      </div>
    )},
  ];

  const tabBtn = (t) => ({
    padding:'9px 18px', fontSize:13, cursor:'pointer', background:'none', border:'none',
    borderBottom:`2px solid ${tab===t?'var(--accent)':'transparent'}`,
    color:tab===t?'var(--accent)':'var(--text-muted)',
    fontWeight:tab===t?600:400, marginBottom:-1, transition:'all 0.15s',
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, borderBottom:'1px solid var(--border)' }}>
        <button style={tabBtn('programs')} onClick={()=>{setTab('programs');setCohortFilter({program_id:''});}}>Programs & Training</button>
        <button style={tabBtn('cohorts')}  onClick={()=>setTab('cohorts')}>
          Cohorts
          {cohortFilter.program_id && <span style={{ marginLeft:6, fontSize:10, padding:'1px 6px', borderRadius:20, background:'rgba(232,164,58,0.2)', color:'var(--accent)', border:'1px solid rgba(232,164,58,0.3)' }}>filtered</span>}
        </button>
      </div>

      {/* ── PROGRAMS TAB ── */}
      {tab==='programs' && (
        <>
          {/* Domain filter cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
            {domains.map((d,i) => {
              const colors = ['var(--blue-light)','var(--green-light)','var(--accent)','var(--yellow)','var(--purple-light)'];
              return (
                <div key={d.id} onClick={()=>setFilter(f=>({...f,domain:f.domain===d.slug?'':d.slug}))}
                  style={{ background:filter.domain===d.slug?'var(--accent-muted)':'var(--bg-surface)', border:`1px solid ${filter.domain===d.slug?'var(--accent)':'var(--border)'}`, borderRadius:'var(--radius-md)', padding:'12px 14px', cursor:'pointer', transition:'all 0.15s' }}>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:colors[i%colors.length], marginBottom:4 }}>Domain {i+1}</div>
                  <div style={{ fontSize:11, color:'var(--text-secondary)', lineHeight:1.4 }}>{d.name}</div>
                </div>
              );
            })}
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <Select value={filter.status} onChange={e=>setFilter(f=>({...f,status:e.target.value}))} style={{ width:160 }}>
                <option value="">All Statuses</option>
                <option value="ongoing">Active / Ongoing</option>
                <option value="draft">Draft</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </Select>
              <span style={{ fontSize:12, color:'var(--text-muted)' }}>{pagination?.total??0} programs</span>
            </div>
            <Button onClick={openCreateProgram}>+ New Program</Button>
          </div>

          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <Table columns={PROG_COLS} data={programs} loading={loading} onRowClick={openEditProgram}/>
            <Pagination pagination={pagination} onPage={setPage}/>
          </div>
        </>
      )}

      {/* ── COHORTS TAB ── */}
      {tab==='cohorts' && (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <Select value={cohortFilter.program_id} onChange={e=>setCohortFilter({program_id:e.target.value})} style={{ width:260 }}>
                <option value="">All Programs</option>
                {allPrograms.map(p=><option key={p.id} value={p.id}>{p.title||p.name}</option>)}
              </Select>
              <span style={{ fontSize:12, color:'var(--text-muted)' }}>{cohorts.length} cohorts</span>
            </div>
            <Button onClick={openCreateCohort}>+ New Cohort</Button>
          </div>
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
            <Table columns={COHORT_COLS} data={cohorts} loading={loading} onRowClick={openEditCohort}/>
          </div>
        </>
      )}

      {/* ── PROGRAM MODAL ── */}
      <Modal open={modal==='program'} onClose={()=>setModal(null)} title={selected?`Edit — ${selected.title||selected.name}`:'New Program'} width={660}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          {error && <div style={{ gridColumn:'1/-1', padding:'10px 14px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:8, fontSize:13, color:'#f06060' }}>⚠ {error}</div>}
          <div style={{ gridColumn:'1/-1' }}>
            <ImagePickerField label="Cover Image" value={form.media_url} onChange={url=>setForm(f=>({...f,media_url:url}))}/>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <Input label="Program Name" value={form.name} onChange={e=>{
              const slug = e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
              setForm(f=>({...f,name:e.target.value,slug}));
            }}/>
          </div>
          <Input label="URL Slug"  value={form.slug}      onChange={e=>setForm(f=>({...f,slug:e.target.value}))} style={{ fontFamily:'monospace' }}/>
          <Select label="Domain"   value={form.domain_id} onChange={e=>setForm(f=>({...f,domain_id:e.target.value}))}>
            {domains.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
          <Select label="Type"     value={form.type}      onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
            {Object.entries(TYPE_LABELS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
          </Select>
          <Select label="Mode"     value={form.mode}      onChange={e=>setForm(f=>({...f,mode:e.target.value}))}>
            {Object.entries(MODE_LABELS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
          </Select>
          <Input label="Duration"  value={form.duration}  onChange={e=>setForm(f=>({...f,duration:e.target.value}))} placeholder="e.g. 12 weeks"/>
          <Select label="Status"   value={form.status}    onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
            <option value="ongoing">Active / Ongoing</option>
            <option value="draft">Draft</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </Select>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={3} style={taStyle}/>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Learning Outcomes (one per line)</label>
            <textarea value={form.outcomes} onChange={e=>setForm(f=>({...f,outcomes:e.target.value}))} rows={4} placeholder="Participants will be able to build full-stack web apps&#10;..." style={{ ...taStyle, fontFamily:'inherit' }}/>
          </div>
          <div style={{ gridColumn:'1/-1', display:'flex', gap:20 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
              <input type="checkbox" checked={form.is_public} onChange={e=>setForm(f=>({...f,is_public:e.target.checked}))}/>
              <span style={{ color:'var(--text-secondary)' }}>Public (visible on SPA)</span>
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
              <input type="checkbox" checked={form.featured} onChange={e=>setForm(f=>({...f,featured:e.target.checked}))}/>
              <span style={{ color:'var(--text-secondary)' }}>Featured on homepage</span>
            </label>
          </div>
          <div style={{ gridColumn:'1/-1', display:'flex', gap:10, justifyContent:'flex-end', paddingTop:6 }}>
            <Button variant="secondary" onClick={()=>setModal(null)}>Cancel</Button>
            <Button onClick={handleSaveProgram} loading={saving}>{selected?'Save Changes':'Create Program'}</Button>
          </div>
        </div>
      </Modal>

      {/* ── COHORT MODAL ── */}
      <Modal open={modal==='cohort'} onClose={()=>setModal(null)} title={selected?`Edit Cohort — ${selected.cohort_name}`:'New Cohort'} width={620}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          {error && <div style={{ gridColumn:'1/-1', padding:'10px 14px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:8, fontSize:13, color:'#f06060' }}>⚠ {error}</div>}
          <div style={{ gridColumn:'1/-1' }}>
            <Select label="Program *" value={cohortForm.program_id} onChange={e=>setCohortForm(f=>({...f,program_id:e.target.value}))}>
              <option value="">— Select program —</option>
              {allPrograms.map(p=><option key={p.id} value={p.id}>{p.title||p.name}</option>)}
            </Select>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <Input label="Cohort Name *" value={cohortForm.cohort_name} onChange={e=>setCohortForm(f=>({...f,cohort_name:e.target.value}))} placeholder="e.g. Cohort 2025-A"/>
          </div>
          <Input label="Start Date" type="date" value={cohortForm.start_date} onChange={e=>setCohortForm(f=>({...f,start_date:e.target.value}))}/>
          <Input label="End Date"   type="date" value={cohortForm.end_date}   onChange={e=>setCohortForm(f=>({...f,end_date:e.target.value}))}/>
          <Input label="Max Capacity" type="number" value={cohortForm.max_capacity} onChange={e=>setCohortForm(f=>({...f,max_capacity:parseInt(e.target.value)||1}))}/>
          <Select label="Status" value={cohortForm.status} onChange={e=>setCohortForm(f=>({...f,status:e.target.value}))}>
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="ongoing">Ongoing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
          <div style={{ gridColumn:'1/-1' }}>
            <Input label="Location" value={cohortForm.location} onChange={e=>setCohortForm(f=>({...f,location:e.target.value}))} placeholder="MilleHills Hub, Gisenyi"/>
          </div>
          <Input label="Instructor" value={cohortForm.instructor} onChange={e=>setCohortForm(f=>({...f,instructor:e.target.value}))} placeholder="Trainer name"/>
          <div style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end', paddingBottom:4 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
              <input type="checkbox" checked={cohortForm.is_free} onChange={e=>setCohortForm(f=>({...f,is_free:e.target.checked,fee_amount:e.target.checked?0:f.fee_amount}))}/>
              <span style={{ color:'var(--text-secondary)' }}>Free cohort</span>
            </label>
          </div>
          {!cohortForm.is_free && <>
            <Input label="Fee Amount" type="number" value={cohortForm.fee_amount} onChange={e=>setCohortForm(f=>({...f,fee_amount:e.target.value}))}/>
            <Select label="Currency" value={cohortForm.fee_currency} onChange={e=>setCohortForm(f=>({...f,fee_currency:e.target.value}))}>
              <option value="RWF">RWF</option>
              <option value="USD">USD</option>
            </Select>
          </>}
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Schedule Info</label>
            <textarea value={cohortForm.schedule_info} onChange={e=>setCohortForm(f=>({...f,schedule_info:e.target.value}))} rows={2} placeholder="e.g. Mon/Wed/Fri 9am-12pm" style={taStyle}/>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={labelStyle}>Notes (internal)</label>
            <textarea value={cohortForm.notes} onChange={e=>setCohortForm(f=>({...f,notes:e.target.value}))} rows={2} style={taStyle}/>
          </div>
          <div style={{ gridColumn:'1/-1', display:'flex', gap:10, justifyContent:'flex-end', paddingTop:6 }}>
            <Button variant="secondary" onClick={()=>setModal(null)}>Cancel</Button>
            <Button onClick={handleSaveCohort} loading={saving}>{selected?'Save Cohort':'Create Cohort'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', display:'block', marginBottom:6 };
const taStyle    = { width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'monospace', boxSizing:'border-box' };